import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Select,
  Text,
  VStack
} from '@chakra-ui/react'
import { useForm, useFieldArray } from 'react-hook-form'
import WarrantyPageLayout from '../warranty/warranty-page-layout'
import { useAlert } from '@common/hooks'
import { BACKEND_URL_STORE } from '@constants/server.constant'
import RouterUrl from '@constants/router.constant'

const ORANGE = '#DE4800'
const ORANGE_HOVER = '#c23f00'

interface PartnerStore {
  id: string
  name: string
}

interface WarrantyForm {
  retailer: string
  your_name: string
  email: string
  products: { name: string }[]
}

const fieldStyle = {
  bg: 'white',
  borderColor: 'gray.200',
  borderRadius: 'md',
  h: '48px',
  fontSize: '16px',
  _hover: { borderColor: 'gray.300' }
}

const WarrantyPartners = () => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<WarrantyForm>({
    defaultValues: { retailer: '', products: [{ name: '' }] }
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products'
  })

  const [retailers, setRetailers] = useState<PartnerStore[]>([])
  const [receipt, setReceipt] = useState<File | null>(null)
  const [receiptError, setReceiptError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const { successToast, errorToast } = useAlert()

  useEffect(() => {
    fetch(`${BACKEND_URL_STORE}/partner-stores`)
      .then(async (res) => await res.json())
      .then((stores: PartnerStore[]) => {
        setRetailers(stores)
        if (stores.length > 0) {
          setValue('retailer', stores[0].id)
        }
      })
      .catch(() => {
        errorToast({
          title: 'Error',
          description: 'Could not load the retailer list. Please reload.'
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = handleSubmit(async (data) => {
    if (!receipt) {
      setReceiptError('Please upload your receipt')
      return
    }
    setReceiptError('')

    const products = data.products.map((p) => p.name).filter(Boolean)

    const formData = new FormData()
    formData.append('email', data.email)
    formData.append('customer_name', data.your_name)
    formData.append('product_name', products.join(', '))
    formData.append('partner_store_id', data.retailer)
    formData.append('receipt_image', receipt)

    try {
      const res = await fetch(`${BACKEND_URL_STORE}/warranties`, {
        method: 'POST',
        body: formData
      })

      if (!res.ok) {
        const body = await res.json().catch(() => null)
        const rawMessage: string = body?.message ?? ''

        // The retailer list may be stale (e.g. a store was removed on the
        // backend). Show a friendly message instead of the raw "Partner
        // store <id> not found" error which the user cannot understand.
        if (
          /partner store/i.test(rawMessage) &&
          /not found/i.test(rawMessage)
        ) {
          throw new Error(
            'The selected retailer is no longer available. Please reload the page and choose again.'
          )
        }

        throw new Error(
          'Sorry, we could not submit your registration. Please check your details and try again.'
        )
      }

      successToast({
        title: 'Registered',
        description:
          'Your warranty registration has been submitted. Please check your email for your discount code.'
      })
      reset({
        retailer: retailers[0]?.id ?? '',
        products: [{ name: '' }],
        your_name: '',
        email: ''
      })
      setReceipt(null)
    } catch (error: any) {
      errorToast({
        title: 'Error',
        description: error?.message ?? 'Something went wrong. Please try again.'
      })
    }
  })

  return (
    <WarrantyPageLayout
      title="Warranty Registration - Retail / Online Partners"
      breadcrumbs={[
        { label: 'Home', href: RouterUrl.HOME.ROOT },
        { label: 'Warranty', href: RouterUrl.WARRANTY },
        { label: 'Retail / Online Partner' }
      ]}
    >
      <VStack spacing={6} w={'100%'} maxW={'600px'} mx={'auto'}>
        <Text textStyle={'tcText'} w={'100%'} textAlign={'center'}>
          Thank You For Purchasing JML&apos;s Product from our partners!
        </Text>
        <Text w={'100%'} textAlign={'center'} color={'red'} fontWeight={600}>
          Warranty coverage is not valid for non-electrical products.
        </Text>

        <Box
          as={'form'}
          onSubmit={onSubmit}
          noValidate
          w={'100%'}
          bg={'white'}
          border={'1px solid'}
          borderColor={'gray.100'}
          borderRadius={'xl'}
          boxShadow={'sm'}
          p={{ base: 5, md: 8 }}
        >
          <VStack spacing={5} align={'stretch'}>
            <FormControl>
              <FormLabel color={'gray.700'} fontWeight={600}>
                Retailer
              </FormLabel>
              <Select
                {...fieldStyle}
                focusBorderColor={ORANGE}
                {...register('retailer', { required: true })}
              >
                {retailers.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isInvalid={!!errors.products?.[0]?.name}>
              <FormLabel color={'gray.700'} fontWeight={600}>
                Product(s) to register
              </FormLabel>
              <VStack spacing={2} align={'stretch'}>
                {fields.map((f, i) => (
                  <Flex key={f.id} gap={2}>
                    <Input
                      {...fieldStyle}
                      focusBorderColor={ORANGE}
                      placeholder="Enter product name"
                      {...register(`products.${i}.name` as const, {
                        required: i === 0 ? 'Please enter a product' : false
                      })}
                    />
                    {i === fields.length - 1 ? (
                      <IconButton
                        aria-label="Add product"
                        icon={<Text fontSize={'24px'}>+</Text>}
                        variant={'outline'}
                        color={ORANGE}
                        borderColor={ORANGE}
                        borderRadius={'md'}
                        minW={'48px'}
                        h={'48px'}
                        _hover={{ bg: ORANGE, color: 'white' }}
                        onClick={() => append({ name: '' })}
                      />
                    ) : (
                      <IconButton
                        aria-label="Remove product"
                        icon={<Text fontSize={'24px'}>−</Text>}
                        variant={'outline'}
                        color={'gray.500'}
                        borderColor={'gray.300'}
                        borderRadius={'md'}
                        minW={'48px'}
                        h={'48px'}
                        _hover={{ bg: 'gray.100' }}
                        onClick={() => remove(i)}
                      />
                    )}
                  </Flex>
                ))}
              </VStack>
              <FormErrorMessage>
                {errors.products?.[0]?.name?.message}
              </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.your_name}>
              <FormLabel color={'gray.700'} fontWeight={600}>
                Your Name
              </FormLabel>
              <Input
                {...fieldStyle}
                focusBorderColor={ORANGE}
                placeholder="Enter your name"
                {...register('your_name', {
                  required: 'Please enter your name'
                })}
              />
              <FormErrorMessage>{errors.your_name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel color={'gray.700'} fontWeight={600}>
                Email
              </FormLabel>
              <Input
                {...fieldStyle}
                focusBorderColor={ORANGE}
                type="email"
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Please enter your email',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email'
                  }
                })}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!receiptError}>
              <FormLabel color={'gray.700'} fontWeight={600}>
                Receipt
              </FormLabel>
              <Flex
                align={'center'}
                justify={'space-between'}
                gap={4}
                border={'1px dashed'}
                borderColor={'gray.300'}
                borderRadius={'md'}
                bg={'gray.50'}
                px={4}
                py={3}
              >
                <Text color={receipt ? 'gray.800' : 'gray.500'} noOfLines={1}>
                  {receipt ? receipt.name : 'No file chosen'}
                </Text>
                <Button
                  flexShrink={0}
                  bg={ORANGE}
                  color={'white'}
                  borderRadius={'md'}
                  px={6}
                  _hover={{ bg: ORANGE_HOVER }}
                  onClick={() => fileRef.current?.click()}
                >
                  UPLOAD
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    setReceipt(e.target.files?.[0] ?? null)
                    setReceiptError('')
                  }}
                />
              </Flex>
              <FormErrorMessage>{receiptError}</FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              isLoading={isSubmitting}
              w={'100%'}
              h={'52px'}
              bg={ORANGE}
              color={'white'}
              borderRadius={'md'}
              fontSize={'18px'}
              fontWeight={700}
              letterSpacing={'wide'}
              _hover={{ bg: ORANGE_HOVER }}
              mt={2}
            >
              REGISTER
            </Button>
          </VStack>
        </Box>
      </VStack>
    </WarrantyPageLayout>
  )
}

export default WarrantyPartners
